import { Action, Reducer } from 'redux';
import { codetableOptionsReducer } from '../common';
import {
  ACTION,
  RequestCodetableAdministrationList, ReceiveCodetableAdministrationList, FailCodetableAdministrationList,
  RequestCodeItemDelete, ReceiveCodeItemDelete, FailCodeItemDelete,
  UpdateIncludeInactive, UpdateSearchModel, ResetListState,
  DuplicateRowsFieldsByKey, ResetValidation,
  RequestCodeItems, ReceiveCodeItems, FailCodeItems,
  RequestDuplicateRows, ReceiveDuplicateRows, FailDuplicateRows,
  UpdateDuplicateRowsValidation, ClearDuplicateRows, ModifyFactor, CancelChanges, RequestSaveFactors, ReceiveSaveFactors,
  FailSaveFactors, FailLoadCodeParameters, ReceiveLoadCodeParameters, RequestLoadCodeParameters, UpdateIsDirty,
  RequestDeleteRows, ReceiveDeleteRows, FailDeleteRows,
  ModifyComments,
  RequestSaveComments,
  ReceiveSaveComments,
  FailSaveComments
} from './action-types';
import { defaultIncludeInactiveCodes } from './constants';
import { clearValidationForId } from './validation';
import { CodetableListState } from './types-store';

export const INITIAL_CODETABLE_LIST_STATE: CodetableListState = {
  requestId: 0,
  data: undefined,
  numberOfResults: undefined,
  itemAccesses: [],
  category: undefined,
  fetchingCodetableId: undefined,
  fetchingBreadcrumbsCodetableId: undefined,
  fetchingDelete: undefined,
  fetchingData: undefined,
  fetchingDuplicateRows: undefined,
  fetchingDeleteRows: undefined,
  fetchingLoadCodeParameters: undefined,
  includeInactive: defaultIncludeInactiveCodes,
  codetableOptions: {},
  options: [],
  requestedSearchModel: undefined,
  duplicateRows: undefined,
  modifiedFactors: [],
  modifiedComments: [],
  validation: {},
  updating: false
};

const reducers: { [indexer: string]: Reducer<CodetableListState, Action> } = {

  ...codetableOptionsReducer as { [indexer: string]: Reducer<CodetableListState> },

  [ACTION.REQUEST_CODE_ITEMS]: (state: CodetableListState, action: RequestCodeItems): CodetableListState => {

    return {
      ...state,
      requestId: action.requestId,
      fetchingCodetableId: action.codetableId,
      fetchingData: true,
      requestedSearchModel: action.model,
      ...(action.clearResults && {
        numberOfResults: undefined,
        data: undefined,
        itemAccesses: [],
        modifiedFactors: [],
        modifiedComments: [],
        validation: {}
      })
    };
  },

  [ACTION.RECEIVE_CODE_ITEMS]: (state: CodetableListState, action: ReceiveCodeItems): CodetableListState => {

    if (action.requestId !== state.requestId) {
      return state;
    }

    return {
      ...state,
      fetchingData: false,
      numberOfResults: action.numberOfResults,
      data: [
        ...(state.data || []),
        ...action.list
      ],
      itemAccesses: [
        ...(state.itemAccesses || []),
        ...action.itemAccesses
      ]
    };
  },

  [ACTION.FAIL_CODE_ITEMS]: (state: CodetableListState, action: FailCodeItems): CodetableListState => {

    if (action.requestId !== state.requestId) {
      return state;
    }

    return {
      ...state,
      fetchingData: false
    };
  },

  [ACTION.REQUEST_CODETABLE_ADMINISTRATION_LIST]:
    (state: CodetableListState, action: RequestCodetableAdministrationList): CodetableListState => {

      return {
        ...state,
        fetchingBreadcrumbsCodetableId: true
      };
    },

  [ACTION.RECEIVE_CODETABLE_ADMINISTRATION_LIST]:
    (state: CodetableListState, action: ReceiveCodetableAdministrationList): CodetableListState => {

      const { options } = action;

      return {
        ...state,
        fetchingBreadcrumbsCodetableId: false,
        options
      };
    },

  [ACTION.FAIL_CODETABLE_ADMINISTRATION_LIST]:
    (state: CodetableListState, action: FailCodetableAdministrationList): CodetableListState => {

      return {
        ...state,
        fetchingBreadcrumbsCodetableId: false,
        options: []
      };
    },

  [ACTION.REQUEST_CODE_ITEM_DELETE]: (state: CodetableListState, action: RequestCodeItemDelete): CodetableListState => {

    return {
      ...state,
      fetchingDelete: action.codeId
    };
  },

  [ACTION.RECEIVE_CODE_ITEM_DELETE]: (state: CodetableListState, action: ReceiveCodeItemDelete): CodetableListState => {

    return {
      ...state,
      fetchingDelete: undefined,
      data: state?.data?.filter(item => item.id !== action.codeId),
      modifiedFactors: (state.modifiedFactors || []).filter(item =>
        item.id !== action.codeId
      ),
      validation: clearValidationForId(state.validation, action.codeId),
      numberOfResults: state.numberOfResults - 1
    };
  },

  [ACTION.FAIL_CODE_ITEM_DELETE]: (state: CodetableListState, action: FailCodeItemDelete): CodetableListState => {

    return {
      ...state,
      fetchingDelete: undefined
    };
  },

  [ACTION.UPDATE_INCLUDE_INACTIVE]: (state: CodetableListState, action: UpdateIncludeInactive): CodetableListState => {

    return {
      ...state,
      includeInactive: action.value
    };
  },

  [ACTION.UPDATE_SEARCH_MODEL]: (state: CodetableListState, action: UpdateSearchModel): CodetableListState => {
    return {
      ...state,
      requestedSearchModel: action.model
    };
  },

  [ACTION.RESET_LIST_STATE]: (state: CodetableListState, action: ResetListState): CodetableListState => {
    return {
      ...INITIAL_CODETABLE_LIST_STATE,
      requestId: state.requestId,
      category: state.category,
      fetchingCodetableId: state.fetchingCodetableId
    };
  },

  [ACTION.DUPLICATE_ROWS_FIELDS_BY_KEY]: (state: CodetableListState, action: DuplicateRowsFieldsByKey): CodetableListState => {
    return {
      ...state,
      duplicateRows: {
        ...state.duplicateRows,
        form: {
          ...state.duplicateRows?.form ?? {},
          [action.key]: action.value
        }
      }
    };
  },

  [ACTION.RESET_VALIDATION]: (state: CodetableListState, action: ResetValidation): CodetableListState => {
    const validation = state?.duplicateRows?.validation;
    const { [action.key]: undefined, ...updatedValidation } = validation ?? {};

    return state?.duplicateRows?.validation ? {
      ...state,
      duplicateRows: {
        ...state.duplicateRows,
        validation: updatedValidation
      }
    } : state;
  },

  [ACTION.UPDATE_IS_DIRTY]: (state: CodetableListState, action: UpdateIsDirty): CodetableListState => {

    return {
      ...state,
      duplicateRows: {
        ...state.duplicateRows,
        isDirty: action.isDirty
      }
    };
  },

  [ACTION.REQUEST_DUPLICATE_ROWS]: (state: CodetableListState, action: RequestDuplicateRows): CodetableListState => {

    return {
      ...state,
      fetchingDuplicateRows: true
    };
  },

  [ACTION.RECEIVE_DUPLICATE_ROWS]: (state: CodetableListState, action: ReceiveDuplicateRows): CodetableListState => {

    return {
      ...state,
      fetchingDuplicateRows: false
    };
  },

  [ACTION.FAIL_DUPLICATE_ROWS]: (state: CodetableListState, action: FailDuplicateRows): CodetableListState => {

    return {
      ...state,
      fetchingDuplicateRows: false
    };
  },

  [ACTION.REQUEST_DELETE_ROWS]: (state: CodetableListState, action: RequestDeleteRows): CodetableListState => {

    return {
      ...state,
      fetchingDeleteRows: true
    };
  },

  [ACTION.RECEIVE_DELETE_ROWS]: (state: CodetableListState, action: ReceiveDeleteRows): CodetableListState => {

    return {
      ...state,
      fetchingDeleteRows: false
    };
  },

  [ACTION.FAIL_DELETE_ROWS]: (state: CodetableListState, action: FailDeleteRows): CodetableListState => {

    return {
      ...state,
      fetchingDeleteRows: false
    };
  },

  [ACTION.UPDATE_DUPLICATE_ROWS_VALIDATION]: (state: CodetableListState, action: UpdateDuplicateRowsValidation): CodetableListState => {

    return {
      ...state,
      duplicateRows: {
        ...state.duplicateRows,
        validation: action.validation
      }
    };
  },

  [ACTION.REQUEST_LOAD_CODE_PARAMETERS]: (state: CodetableListState, action: RequestLoadCodeParameters): CodetableListState => {

    return {
      ...state,
      duplicateRows: {
        ...state.duplicateRows,
        fetchingLoadCodeParameters: true
      }
    };
  },

  [ACTION.RECEIVE_LOAD_CODE_PARAMETERS]: (state: CodetableListState, action: ReceiveLoadCodeParameters): CodetableListState => {

    return {
      ...state,
      duplicateRows: {
        ...state.duplicateRows,
        fetchingLoadCodeParameters: false,
        codeParameters: action.parameters
      }
    };
  },

  [ACTION.FAIL_LOAD_CODE_PARAMETERS]: (state: CodetableListState, action: FailLoadCodeParameters): CodetableListState => {

    return {
      ...state,
      fetchingLoadCodeParameters: false
    };
  },

  [ACTION.CLEAR_DUPLICATE_ROWS]: (state: CodetableListState, action: ClearDuplicateRows): CodetableListState => {

    return {
      ...state,
      duplicateRows: undefined
    };
  },

  [ACTION.MODIFY_FACTOR]: (state: CodetableListState, action: ModifyFactor): CodetableListState => {

    const modifiedData = action.modifiedData;
    const persistedValue = state.data?.find(codeItem => codeItem.id === action.modifiedData.id);

    if (!persistedValue) {
      return state;
    }

    const previousModifiedItem = state.modifiedFactors?.find(
      modifiedItem => !!modifiedItem && modifiedItem.id === action.modifiedData.id
    );

    const validation = clearValidationForId(state.validation, action.modifiedData.id);

    if (modifiedData.factor === persistedValue.factor) {
      return {
        ...state,
        modifiedFactors: (state.modifiedFactors || []).filter(item =>
          item !== previousModifiedItem
        ),
        validation
      };
    }

    return {
      ...state,
      modifiedFactors: [
        ...(state.modifiedFactors || []).filter(item =>
          item !== previousModifiedItem
        ),
        modifiedData
      ],
      validation
    };
  },

  [ACTION.MODIFY_COMMENTS]: (state: CodetableListState, action: ModifyComments): CodetableListState => {

    const modifiedData = action.modifiedData;
    const persistedValue = state.data?.find(codeItem => codeItem.id === action.modifiedData.id);

    if (!persistedValue) {
      return state;
    }

    const previousModifiedItem = state.modifiedComments?.find(
      modifiedItem => !!modifiedItem && modifiedItem.id === action.modifiedData.id
    );

    const validation = clearValidationForId(state.validation, action.modifiedData.id);

    if (modifiedData.comments === persistedValue.comments) {
      return {
        ...state,
        modifiedComments: (state.modifiedComments || []).filter(item =>
          item !== previousModifiedItem
        ),
        validation
      };
    }

    return {
      ...state,
      modifiedComments: [
        ...(state.modifiedComments || []).filter(item =>
          item !== previousModifiedItem
        ),
        modifiedData
      ],
      validation
    };
  },

  [ACTION.CANCEL_CHANGES]: (state: CodetableListState, action: CancelChanges): CodetableListState => {

    return {
      ...state,
      modifiedFactors: [],
      modifiedComments: [],
      validation: {}
    };
  },

  [ACTION.REQUEST_SAVE_FACTORS]: (state: CodetableListState, action: RequestSaveFactors): CodetableListState => {

    return {
      ...state,
      updating: true
    };
  },

  [ACTION.RECEIVE_SAVE_FACTORS]: (state: CodetableListState, action: ReceiveSaveFactors): CodetableListState => {

    return {
      ...state,
      data: (state.data || []).map(codeItem => {
        const modifiedItem = state.modifiedFactors?.find(item => item.id === codeItem.id);

        return modifiedItem
          ? {
            ...codeItem,
            factor: modifiedItem.factor
          }
          : codeItem;
      }),
      modifiedFactors: [],
      updating: false
    };
  },

  [ACTION.FAIL_SAVE_FACTORS]: (state: CodetableListState, action: FailSaveFactors): CodetableListState => {

    return {
      ...state,
      updating: false,
      validation: action.validation
    };
  },

  [ACTION.REQUEST_SAVE_COMMENTS]: (state: CodetableListState, action: RequestSaveComments): CodetableListState => {

    return {
      ...state,
      updating: true
    };
  },

  [ACTION.RECEIVE_SAVE_COMMENTS]: (state: CodetableListState, action: ReceiveSaveComments): CodetableListState => {

    return {
      ...state,
      data: (state.data || []).map(codeItem => {
        const modifiedItem = state.modifiedComments?.find(item => item.id === codeItem.id);

        return modifiedItem
          ? {
            ...codeItem,
            comments: modifiedItem.comments
          }
          : codeItem;
      }),
      modifiedComments: [],
      updating: false
    };
  },

  [ACTION.FAIL_SAVE_COMMENTS]: (state: CodetableListState, action: FailSaveComments): CodetableListState => {

    return {
      ...state,
      updating: false,
      validation: action.validation
    };
  }
};

export const listReducer: Reducer<CodetableListState> = (
  state: CodetableListState = INITIAL_CODETABLE_LIST_STATE, action: any) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};
